import { Dialog } from "primereact/dialog";
import "./tenantModals.scss";
import { useEffect, useState } from "react";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import CustomeButton from "~/components/Button";
import { RadioButton } from "primereact/radiobutton";
import {
  AssignUserModalProps,
  SelectedUser,
  SelectedUserOption,
} from "../types/tenants";
import useTenants from "../useTenants";
import { useQueryClient } from "@tanstack/react-query";

const AssignUserModal = ({
  visible,
  closeModal,
  toastRef,
  tenantId,
  fetchTenantUsers,
}: AssignUserModalProps) => {
  const [selectedUserOption, setSelectedUserOption] =
    useState<SelectedUserOption | null>(null);
  const [selectedUser, setSelectedUser] = useState<SelectedUser | null>(null);
  const [userOptions, setUserOptions] = useState<SelectedUserOption[]>([]);
  const [totalUsers, setTotalusers] = useState<SelectedUser[]>([]);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loading, setIsLoading] = useState(false);
  const [isusersLoading, setIsUsersLoading] = useState(false);
  const { fetchActiveUsers, updateUserTenants } = useTenants();
  const queryClient = useQueryClient();

  // Fetch users only when modal becomes visible
  useEffect(() => {
    if (visible) {
      fetchUsers();
    }
  }, [visible]);

  // Reset form when modal closes
  useEffect(() => {
    if (!visible) {
      setSelectedUserOption(null);
      setSelectedUser(null);
      setButtonDisabled(true);
      setError(null);
    }
  }, [visible]);

  const fetchUsers = async () => {
    try {
      setIsUsersLoading(true);
      // GXM-1800: Use sparse fieldset to optimize response size
      // Request only fields needed for the assign user modal:
      // - id: for user selection
      // - first_name, last_name, email: for dropdown display
      // - tenants: to check if user is already assigned to this tenant
      const fields = "id,first_name,last_name,email,tenants";
      const response = await fetchActiveUsers(false, fields);
      const usersList = response.data?.users || [];
      // transform users to dropdown options
      const userOptions = usersList.map((user: SelectedUser) => ({
        name: `${user.first_name} ${user.last_name} (${user.email})`,
        code: user.id,
      }));
      setTotalusers(usersList);
      setUserOptions(userOptions);
      setIsUsersLoading(false);
    } catch (error) {
      console.error(error);
      setIsUsersLoading(false);
      if (toastRef?.current) {
        toastRef.current?.show({
          severity: "error",
          icon: "pi pi-times-circle",
          summary: "Failed to fetch users. Please try again later.",
          life: 5000,
        });
      }
    }
  };

  const selectUserHandler = (e: DropdownChangeEvent) => {
    setSelectedUserOption(e.value);
    // check if selected user is already assigned to this tenant
    const userInfo = totalUsers.find((user) => user.id === e.value.code);
    const isUserAssigned = userInfo?.tenants?.some(
      (tenant) => tenant === tenantId,
    );
    if (isUserAssigned) {
      setButtonDisabled(true);
      setError("This user is already assigned to this tenant.");
    } else {
      setError(null);
      setButtonDisabled(false);
      if (userInfo && tenantId) {
        const updatedUser = {
          ...userInfo,
          tenants: [...(userInfo.tenants ?? []), tenantId],
        };
        setSelectedUser(updatedUser);
      }
    }
  };

  const assignUserHandler = async () => {
    try {
      if (!selectedUser) {
        throw new Error(
          "User is not selected .Please select a user to assign.",
        );
      }
      setIsLoading(true);
      const response = await updateUserTenants(selectedUser);
      if (response.success) {
        if (toastRef?.current && selectedUser) {
          toastRef.current.show({
            severity: "success",
            icon: "pi pi-check",
            summary: `'${selectedUser.first_name} ${selectedUser.last_name}' has been successfully assigned to the tenant.`,
            life: 5000,
          });
        }
        fetchTenantUsers?.();
        // Invalidate audit logs cache to refresh after assignment
        queryClient.invalidateQueries({
          queryKey: ["tenant-user-assignments-audit", tenantId],
        });
        closeModal();
      }
    } catch (error) {
      console.error(error);
      if (toastRef?.current) {
        toastRef.current?.show({
          severity: "error",
          icon: "pi pi-times-circle",
          summary: "Oops! Something went wrong while assigning the user.",
          detail: "Please try again.",
          life: 5000,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const footerTemplate = () => {
    return (
      <div className="flex justify-content-end gap-2">
        <CustomeButton
          label="Cancel"
          variant="outlined"
          className="cancel-button"
          onClick={closeModal}
        />
        <CustomeButton
          label="Assign User"
          variant="contained"
          className="assign-button"
          onClick={assignUserHandler}
          disabled={!selectedUserOption || buttonDisabled || loading}
          loading={loading}
        />
      </div>
    );
  };

  const OptionsTemplate = (option: SelectedUserOption) => {
    return (
      <div className="flex align-items-center gap-2">
        <RadioButton
          checked={
            !!(selectedUserOption && selectedUserOption.code === option.code)
          }
        />
        <span>{option.name}</span>
      </div>
    );
  };

  return (
    <Dialog
      visible={visible}
      onHide={closeModal}
      draggable={false}
      style={{ width: "644px" }}
      className="assign-user-modal"
      footer={footerTemplate}
    >
      <div className="flex flex-column gap-3">
        <div className="header flex flex-column gap-2">
          <p className="title m-0">Assign GXM User to Tenant</p>
          <p className="description m-0">
            Select an active user to assign them to this tenant.
          </p>
        </div>
        <div className="user-input-container flex flex-column gap-2">
          <label className="user-label" htmlFor="user-dropdown">
            GXM User Email *
          </label>
          <Dropdown
            id="user-dropdown"
            value={selectedUserOption}
            options={userOptions}
            onChange={selectUserHandler}
            optionLabel="name"
            placeholder="Select a GXM active user"
            itemTemplate={OptionsTemplate}
            filter
            disabled={isusersLoading}
            loading={isusersLoading}
          />
          {error && <p className="error-message m-0">{error}</p>}
        </div>
      </div>
    </Dialog>
  );
};

export default AssignUserModal;
